import React, { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { useDispatch } from 'react-redux';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { useActivateProjectMutation, useGetProjectByIdQuery } from 'src/services';
import { useNotification } from 'src/hooks/useNotification';
import { useLoader } from 'src/provider/LoaderProvider';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { clsx } from 'clsx';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';

const CreateProjectStep3Page = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState(null);
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const notification = useNotification();
  const loader = useLoader();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useDispatch();
  const [
    activateProject,
    { isLoading: isLoadingActivateProject, isSuccess: isSuccessActivateProject },
  ] = useActivateProjectMutation();

  useEffect(() => {
    if (isSuccessActivateProject) {
      notification.success('Project has been activated!');
    }
  }, [isSuccessActivateProject]);

  useEffect(() => {
    if (isLoadingActivateProject) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingActivateProject]);

  useEffect(() => {
    if (currentProject && !currentProject.is_active) {
      activateProject({ projectId: id });
    }
  }, [currentProject]);

  useEffect(() => {
    const config = creationFlowState[id];

    setValue(config?.promptType || 'system');

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          promptType: value,
        }),
      );
    }
  }, [creationFlowState]);

  useEffect(() => {
    if (value) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
          promptType: value,
        }),
      );
    }
  }, [value]);

  const next = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step + 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  const back = () => {
    navigate(
      projectTypeVar === ProductsType.customer
        ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step - 1}?type=${projectTypeVar}`
        : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        <CreateProjectStepsTitle step={+step} title={'AI Persona Setup Method'} />
        <CreateProjectHead step={+step} titleIndex={2} />
        <div className={'flex flex-col w-[455px]'}>
          <RadioGroup
            defaultValue={'system'}
            onChange={setValue}
            onValueChange={setValue}
            value={value}
            className="flex flex-col space-y-1"
          >
            <label
              htmlFor="system"
              className={'flex flex-col w-full gap-1.5 group/label hover:cursor-pointer'}
            >
              <div className={clsx('flex items-center space-x-5 space-y-0')}>
                <RadioGroupItem value={'system'} id="system" />
                <p className="font-medium text-base text-ebony opacity-70 group-hover/label:opacity-100 transition">
                  Use System Prompt Builder 🏗️
                </p>
              </div>
              <div className={'pl-9'}>
                <p className={'text-ebony_opacity_50 text-sm font-medium'}>
                  Create the AI system prompt through a guided, step-by-step flow with built-in best
                  practices. Ideal if you want a fast, reliable setup.
                </p>
              </div>
            </label>

            <label
              htmlFor="manual"
              className={'flex flex-col w-full gap-1.5 group/label hover:cursor-pointer'}
            >
              <div className={clsx('flex items-center space-x-5 space-y-0')}>
                <RadioGroupItem value={'manual'} id="manual" />
                <p className="font-medium text-base text-ebony opacity-70 group-hover/label:opacity-100 transition">
                  Write System Prompt Manually ✍️
                </p>
              </div>
              <div className={'pl-9'}>
                <p className={'text-ebony_opacity_50 text-sm font-medium'}>
                  Start with a blank editor and craft the AI system prompt line by line. Ideal if
                  you prefer full control and already know how to structure effective prompts.
                </p>
              </div>
            </label>
          </RadioGroup>
        </div>
        <div className={'w-[455px] flex flex-col gap-5 items-center'}>
          <Button onClick={next} className="min-w-24 w-full flex flex-row h-12">
            Continue
          </Button>
          <div
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </div>
        </div>
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep3Page;
